// Agent Inspector — the operator-facing window into a single agent's work
// inside a run. Renders as a modal with four tabs:
//
//   Mission         the agent's system prompt (its role), rendered as
//                   the role statement plus the static description from
//                   the graph. This is "who this agent is."
//
//   Brief received  the full data the agent's fn was invoked with —
//                   brand context, upstream artifacts, operator hint.
//                   This is "what this agent was told."
//
//   Work produced   agent-specific rendering of the structured output —
//                   role-typed slide cards for the Writer, arc outline
//                   for the Researcher, verdict card for the Empathy
//                   Critic, etc. Falls back to JSON for agents without
//                   a custom renderer.
//
//   Raw exchange    the literal LLM exchange: system prompt + user
//                   prompt + raw response, captured by llm/index.ts.
//                   For deep debugging.
//
// Opened from the inspector icon on every event row in the Pipeline
// run trace, and from every node card in the Pipeline graph (when the
// node has fired during the most recent run).

import { useEffect, useState, type ReactNode } from 'react';
import type { GraphNode, LlmCall, PersistedPipelineEvent } from '../lib/pipeline';

type TabKey = 'mission' | 'brief' | 'work' | 'raw';

interface Props {
  open: boolean;
  onClose: () => void;
  /** The agent event being inspected — provides assembledInput + output. */
  event: PersistedPipelineEvent | null;
  /** Graph node metadata for the agent (label, llm/code, description). */
  node: GraphNode | null;
  /** LLM calls associated with this event's node in this run. Usually 1,
   *  occasionally 0 (no-LLM agents) or 2+ (multi-call agents). */
  llmCalls: LlmCall[];
}

export function AgentInspector({ open, onClose, event, node, llmCalls }: Props) {
  const [tab, setTab] = useState<TabKey>('mission');

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open || !event || !node) return null;

  const primaryCall = llmCalls.find((c) => c.kind === 'complete') ?? null;
  const verdict = pickVerdict(event);

  return (
    <div
      className="fixed inset-0 z-50 bg-background-dark/70 backdrop-blur-[2px] flex items-center justify-center md:p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="agent-inspector-title"
        onClick={(e) => e.stopPropagation()}
        className="w-full h-full md:h-auto md:max-w-5xl md:max-h-[92vh] flex flex-col bg-surface md:border border-border-color md:rounded-xl shadow-2xl shadow-black/60 overflow-hidden"
      >
        {/* Header */}
        <header className="flex items-start justify-between gap-3 px-4 md:px-6 py-4 md:py-5 border-b border-border-color">
          <div className="min-w-0">
            <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-accent-cyan">
              Agent Inspector · {event.eventType}
            </p>
            <h2 id="agent-inspector-title" className="font-display text-2xl font-bold tracking-tight mt-1 text-text-primary">
              {node.label}
            </h2>
            <p className="text-sm text-muted mt-1 leading-relaxed max-w-2xl">
              {node.description}
            </p>
            <div className="flex flex-wrap items-center gap-3 mt-2 font-mono text-[10px] uppercase tracking-wider text-muted">
              <span className={node.llm === 'yes' ? 'text-accent-cyan' : node.llm === 'mixed' ? 'text-accent-amber' : 'text-muted'}>
                {node.llm === 'yes' ? 'LLM' : node.llm === 'mixed' ? 'Mixed' : 'Code'}
              </span>
              {event.durationMs != null && <span>· {event.durationMs}ms</span>}
              {primaryCall && (
                <>
                  <span>· {primaryCall.model}</span>
                  <span>· {primaryCall.promptTokens}+{primaryCall.completionTokens} tok</span>
                  <span>· ${Number(primaryCall.usdCost).toFixed(4)}</span>
                </>
              )}
              {verdict && <span className={verdict.tone}>· {verdict.text}</span>}
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="material-symbols-outlined text-muted hover:text-text-primary transition-colors -mt-1"
          >
            close
          </button>
        </header>

        {/* Tabs */}
        <div className="border-b border-border-color px-6 flex gap-1">
          <Tab active={tab === 'mission'} onClick={() => setTab('mission')}>Mission</Tab>
          <Tab active={tab === 'brief'} onClick={() => setTab('brief')}>Brief received</Tab>
          <Tab active={tab === 'work'} onClick={() => setTab('work')}>Work produced</Tab>
          <Tab active={tab === 'raw'} onClick={() => setTab('raw')}>
            Raw exchange {primaryCall ? null : <span className="text-muted/60">·</span>}
          </Tab>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto px-6 py-5">
          {tab === 'mission' && <MissionTab node={node} primaryCall={primaryCall} />}
          {tab === 'brief' && <BriefTab event={event} primaryCall={primaryCall} />}
          {tab === 'work' && <WorkTab nodeId={node.id} event={event} />}
          {tab === 'raw' && <RawTab primaryCall={primaryCall} llmCalls={llmCalls} />}
        </div>

        <footer className="px-6 py-3 border-t border-border-color bg-surface-2/40 flex items-center justify-between">
          <p className="font-mono text-[10px] uppercase tracking-wider text-muted">
            run {event.runId.slice(0, 8)} · node {node.id} · Esc to close
          </p>
        </footer>
      </div>
    </div>
  );
}

function Tab({ children, active, onClick }: { children: ReactNode; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`py-3 px-4 font-mono text-[11px] uppercase tracking-wider border-b-2 transition-colors ${
        active
          ? 'border-accent-cyan text-text-primary'
          : 'border-transparent text-muted hover:text-text-primary'
      }`}
    >
      {children}
    </button>
  );
}

function MissionTab({ node, primaryCall }: { node: GraphNode; primaryCall: LlmCall | null }) {
  if (!primaryCall?.systemPrompt) {
    return (
      <div className="space-y-4">
        <SectionLabel>Role</SectionLabel>
        <p className="text-sm text-text-primary leading-relaxed">{node.description}</p>
        <SectionLabel>Note</SectionLabel>
        <p className="text-xs text-muted leading-relaxed">
          This agent does not make an LLM call directly, so there is no system prompt to render. The "Brief received" tab still shows everything the agent was given to work with.
        </p>
      </div>
    );
  }
  return (
    <div className="space-y-4">
      <SectionLabel>Role</SectionLabel>
      <p className="text-sm text-text-primary leading-relaxed">{node.description}</p>
      <SectionLabel>System prompt</SectionLabel>
      <PromptBlock content={primaryCall.systemPrompt} />
    </div>
  );
}

function BriefTab({ event, primaryCall }: { event: PersistedPipelineEvent; primaryCall: LlmCall | null }) {
  const assembled = event.assembledInput;
  const hasAssembled = assembled != null && (typeof assembled !== 'object' || Object.keys(assembled as object).length > 0);
  return (
    <div className="space-y-5">
      {hasAssembled && (
        <>
          <SectionLabel>Brief received (structured)</SectionLabel>
          <p className="text-xs text-muted leading-relaxed mb-2">
            The exact data the agent was given to work with — brand context, upstream artifacts, operator hint, everything.
          </p>
          <BriefRenderer value={assembled} />
        </>
      )}
      {primaryCall?.userPrompt && (
        <>
          <SectionLabel>Assembled user prompt (what the LLM literally saw)</SectionLabel>
          <PromptBlock content={primaryCall.userPrompt} />
        </>
      )}
      {!hasAssembled && !primaryCall?.userPrompt && (
        <p className="text-xs text-muted">No assembled brief captured for this event (older event from before the Inspector was wired up). Future runs will show the full context here.</p>
      )}
    </div>
  );
}

function WorkTab({ nodeId, event }: { nodeId: string; event: PersistedPipelineEvent }) {
  const output = event.output;
  if (event.eventType === 'failed') {
    return (
      <div className="space-y-3">
        <SectionLabel tone="red">Failure</SectionLabel>
        <pre className="text-xs text-accent-red whitespace-pre-wrap leading-relaxed font-mono">{event.errorMessage ?? '(no error message)'}</pre>
      </div>
    );
  }
  if (output == null) {
    return <p className="text-xs text-muted">No output recorded for this event yet (still running, or this agent doesn't return structured output).</p>;
  }
  // Per-agent custom renderers — fall through to JSON dump as the catch-all.
  return (
    <div className="space-y-4">
      <SectionLabel>Output</SectionLabel>
      {renderOutputForNode(nodeId, output)}
    </div>
  );
}

function RawTab({ primaryCall, llmCalls }: { primaryCall: LlmCall | null; llmCalls: LlmCall[] }) {
  if (llmCalls.length === 0) {
    return <p className="text-xs text-muted">This agent didn't make an LLM call. Nothing to show in the raw exchange.</p>;
  }
  const call = primaryCall ?? llmCalls[0]!;
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 text-xs text-muted font-mono">
        <Stat label="model" value={call.model} />
        <Stat label="kind" value={call.kind} />
        <Stat label="prompt tokens" value={String(call.promptTokens)} />
        <Stat label="completion tokens" value={String(call.completionTokens)} />
        <Stat label="latency" value={`${call.latencyMs}ms`} />
        <Stat label="usd cost" value={`$${Number(call.usdCost).toFixed(6)}`} />
      </div>
      {call.systemPrompt && (
        <>
          <SectionLabel>System prompt</SectionLabel>
          <PromptBlock content={call.systemPrompt} />
        </>
      )}
      {call.userPrompt && (
        <>
          <SectionLabel>User prompt</SectionLabel>
          <PromptBlock content={call.userPrompt} />
        </>
      )}
      {call.rawResponse && (
        <>
          <SectionLabel>Raw response</SectionLabel>
          <PromptBlock content={call.rawResponse} />
        </>
      )}
      {llmCalls.length > 1 && (
        <p className="text-xs text-muted">
          This agent made {llmCalls.length} LLM calls in this run; only the first is shown above.
        </p>
      )}
    </div>
  );
}

// ─────────────────────────── Helpers ───────────────────────────

function SectionLabel({ children, tone }: { children: ReactNode; tone?: 'red' | 'emerald' }) {
  const colour = tone === 'red'
    ? 'text-accent-red'
    : tone === 'emerald'
      ? 'text-accent-emerald'
      : 'text-accent-cyan';
  return (
    <p className={`font-mono text-[10px] uppercase tracking-[0.18em] ${colour}`}>
      {children}
    </p>
  );
}

function PromptBlock({ content }: { content: string }) {
  return (
    <pre className="text-[12px] leading-relaxed whitespace-pre-wrap break-words font-mono bg-background-dark border border-border-color rounded p-3 max-h-[420px] overflow-auto scroll-thin text-text-primary">
      {content}
    </pre>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="border border-border-color rounded p-2 bg-surface-2/40">
      <p className="text-[9px] uppercase tracking-wider text-muted">{label}</p>
      <p className="text-text-primary mt-1">{value}</p>
    </div>
  );
}

function pickVerdict(event: PersistedPipelineEvent): { text: string; tone: string } | null {
  const out = event.output as Record<string, unknown> | null;
  if (!out) return null;
  if (typeof out.verdict === 'string') {
    const v = out.verdict;
    const tone = v === 'helpful' || v === 'pass' ? 'text-accent-emerald' : v === 'tone_deaf' || v === 'fail' ? 'text-accent-red' : 'text-accent-amber';
    return { text: v, tone };
  }
  return null;
}

// ───────────────────── Per-agent output renderers ─────────────────────
// Each one renders the agent's output in a way that says "this is what a
// strategist / writer / editor / etc actually produced" — readable to a
// human, not a JSON dump. Falls back to JSON when there's no specialised
// renderer.

function renderOutputForNode(nodeId: string, output: unknown): ReactNode {
  switch (nodeId) {
    case 'planner':       return <PlannerOutput value={output} />;
    case 'researcher':    return <ResearcherOutput value={output} />;
    case 'hookSmith':     return <SmithOutput value={output} kind="hook" />;
    case 'ctaSmith':      return <SmithOutput value={output} kind="cta" />;
    case 'reelHookSmith': return <SmithOutput value={output} kind="reel-hook" />;
    case 'reelCtaSmith':  return <SmithOutput value={output} kind="reel-cta" />;
    case 'reelTightener': return <ReelTightenerOutput value={output} />;
    case 'writer':        return <WriterOutput value={output} />;
    case 'designer':      return <DesignerOutput value={output} />;
    case 'empathy':       return <EmpathyOutput value={output} />;
    case 'safety':        return <SafetyOutput value={output} />;
    case 'editor':        return <EditorOutput value={output} />;
    case 'finalReader':   return <FinalReaderOutput value={output} />;
    case 'reelFinalReader': return <FinalReaderOutput value={output} />;
    case 'storyboard':    return <StoryboardOutput value={output} />;
    case 'reelScriptWriter': return <ReelScriptOutput value={output} />;  // v15 historical runs only
    case 'reelArtDirector':  return <ReelArtDirectorOutput value={output} />;
    case 'reelVoiceSynthesizer': return <ReelVoiceSynthesizerOutput value={output} />;
    case 'reelMusicComposer': return <ReelMusicComposerOutput value={output} />;
    default:              return <JsonFallback value={output} />;
  }
}

interface AnyObj { [k: string]: unknown }
function asObj(v: unknown): AnyObj | null {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as AnyObj) : null;
}
function asArr<T = unknown>(v: unknown): T[] | null {
  return Array.isArray(v) ? (v as T[]) : null;
}

function JsonFallback({ value }: { value: unknown }) {
  return (
    <pre className="text-[11px] leading-relaxed whitespace-pre-wrap break-words font-mono bg-background-dark border border-border-color rounded p-3 max-h-[460px] overflow-auto scroll-thin text-muted">
      {JSON.stringify(value, null, 2)}
    </pre>
  );
}

function PlannerOutput({ value }: { value: unknown }) {
  const o = asObj(value);
  if (!o) return <JsonFallback value={value} />;
  const persona = asObj(o.persona);
  const theme = asObj(o.theme);
  return (
    <div className="space-y-3 text-sm text-text-primary">
      <Field label="Format" value={String(o.format ?? '—')} />
      <Field label="Kind" value={String(o.kind ?? '—')} />
      {persona && <Field label="Persona" value={`${persona.name} — ${persona.description}`} />}
      {theme && <Field label="Theme" value={`${theme.label} — ${theme.description}`} />}
    </div>
  );
}

function ResearcherOutput({ value }: { value: unknown }) {
  const o = asObj(value);
  const arc = asArr<AnyObj>(o?.arc);
  if (!o || !arc) return <JsonFallback value={value} />;
  return (
    <div className="space-y-4 text-sm">
      <Field label="Angle" value={String(o.angle ?? '—')} />
      <SectionLabel>Slide arc</SectionLabel>
      <ol className="space-y-2">
        {arc.map((entry, i) => (
          <li key={i} className="rounded border border-border-color p-3 bg-surface-2/40">
            <div className="flex items-baseline gap-3">
              <span className="font-mono text-[10px] uppercase tracking-wider text-accent-cyan">{i + 1}</span>
              <span className="font-mono text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-accent-cyan/15 text-accent-cyan">{String(entry.role ?? 'body')}</span>
              <span className="text-xs text-muted">{String(entry.purpose ?? '')}</span>
            </div>
            <p className="mt-2 text-sm text-text-primary leading-relaxed">{String(entry.beatBody ?? '')}</p>
          </li>
        ))}
      </ol>
      {asArr<string>(o.evidence) && (o.evidence as string[]).length > 0 && (
        <>
          <SectionLabel>Evidence</SectionLabel>
          <ul className="list-disc list-inside text-xs text-muted space-y-0.5">
            {(o.evidence as string[]).map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        </>
      )}
      {asArr<string>(o.notes) && (o.notes as string[]).length > 0 && (
        <>
          <SectionLabel>Notes / pitfalls</SectionLabel>
          <ul className="list-disc list-inside text-xs text-muted space-y-0.5">
            {(o.notes as string[]).map((n, i) => <li key={i}>{n}</li>)}
          </ul>
        </>
      )}
    </div>
  );
}

function WriterOutput({ value }: { value: unknown }) {
  const o = asObj(value);
  const slides = asArr<AnyObj>(o?.slides);
  if (!o || !slides) return <JsonFallback value={value} />;
  return (
    <div className="space-y-3">
      <Field label="Topic" value={String(o.topicTitle ?? '—')} />
      <Field label="Format" value={String(o.format ?? '—')} />
      <SectionLabel>Slides</SectionLabel>
      <ol className="space-y-3">
        {slides.map((s, i) => (
          <li key={i} className="rounded border border-border-color p-3 bg-surface-2/40">
            <div className="flex items-baseline gap-2 mb-2">
              <span className="font-mono text-[10px] text-muted">{i + 1}</span>
              {Boolean(s.role) && <span className="font-mono text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-accent-cyan/15 text-accent-cyan">{String(s.role)}</span>}
              {Boolean(s.kind) && <span className="font-mono text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-accent-amber/15 text-accent-amber">{String(s.kind)}</span>}
            </div>
            {Boolean(s.title) && <h4 className="font-display font-bold text-text-primary text-base mb-1">{String(s.title)}</h4>}
            <p className="text-sm text-text-primary leading-relaxed whitespace-pre-wrap">{String(s.body ?? '')}</p>
            <p className="mt-2 font-mono text-[9px] uppercase tracking-wider text-muted/70">
              title {String(s.title ?? '').length} · body {String(s.body ?? '').length} chars
            </p>
          </li>
        ))}
      </ol>
    </div>
  );
}

function DesignerOutput({ value }: { value: unknown }) {
  return <WriterOutput value={value} />;
}

function EmpathyOutput({ value }: { value: unknown }) {
  const o = asObj(value);
  if (!o) return <JsonFallback value={value} />;
  const verdict = String(o.verdict ?? '—');
  const tone = verdict === 'helpful' ? 'bg-accent-emerald/20 text-accent-emerald' : verdict === 'tone_deaf' ? 'bg-accent-red/20 text-accent-red' : 'bg-accent-amber/20 text-accent-amber';
  return (
    <div className="space-y-3 text-sm">
      <div>
        <p className="font-mono text-[10px] uppercase tracking-wider text-muted mb-1">Verdict</p>
        <span className={`inline-block font-mono text-xs uppercase tracking-wider px-3 py-1 rounded ${tone}`}>{verdict}</span>
      </div>
      {Boolean(o.reason) && (
        <Field label="Reason" value={String(o.reason)} />
      )}
      {asArr<string>(o.violations) && (o.violations as string[]).length > 0 && (
        <>
          <SectionLabel tone="red">Violations</SectionLabel>
          <ul className="list-disc list-inside text-xs text-text-primary space-y-0.5">
            {(o.violations as string[]).map((v, i) => <li key={i}>{v}</li>)}
          </ul>
        </>
      )}
    </div>
  );
}

function SafetyOutput({ value }: { value: unknown }) {
  const o = asObj(value);
  if (!o) return <JsonFallback value={value} />;
  const verdict = String(o.verdict ?? '—');
  const tone = verdict === 'pass' ? 'bg-accent-emerald/20 text-accent-emerald' : 'bg-accent-red/20 text-accent-red';
  return (
    <div className="space-y-3 text-sm">
      <div>
        <p className="font-mono text-[10px] uppercase tracking-wider text-muted mb-1">Verdict</p>
        <span className={`inline-block font-mono text-xs uppercase tracking-wider px-3 py-1 rounded ${tone}`}>{verdict}</span>
      </div>
      {asArr<string>(o.reasons) && (o.reasons as string[]).length > 0 && (
        <>
          <SectionLabel tone="red">Reasons</SectionLabel>
          <ul className="list-disc list-inside text-xs text-text-primary space-y-0.5">
            {(o.reasons as string[]).map((r, i) => <li key={i}>{r}</li>)}
          </ul>
        </>
      )}
    </div>
  );
}

function EditorOutput({ value }: { value: unknown }) {
  return <WriterOutput value={value} />;
}

function SmithOutput({ value, kind }: { value: unknown; kind: 'hook' | 'cta' | 'reel-hook' | 'reel-cta' }) {
  const o = asObj(value);
  if (!o) return <JsonFallback value={value} />;
  const candidates = asArr<AnyObj>(o.candidates) ?? [];
  const chosen = typeof o.chosen === 'number' ? o.chosen : 0;
  const label =
    kind === 'hook' ? 'Hook candidates'
    : kind === 'cta' ? 'CTA candidates'
    : kind === 'reel-hook' ? 'Opening-line candidates'
    : 'Closing-line candidates';
  return (
    <div className="space-y-4 text-sm">
      <div>
        <p className="font-mono text-[10px] uppercase tracking-wider text-muted mb-2">{label}</p>
        <div className="space-y-2">
          {candidates.map((c, i) => {
            const picked = i === chosen;
            return (
              <div
                key={i}
                className={`rounded border p-3 ${picked ? 'border-accent-emerald/60 bg-accent-emerald/5' : 'border-border-color bg-surface'}`}
              >
                <div className="flex items-start justify-between gap-2 mb-1">
                  <span className="font-mono text-[10px] uppercase tracking-wider text-muted">
                    {String(c.approach ?? `option ${i + 1}`)}
                  </span>
                  {picked && (
                    <span className="font-mono text-[10px] uppercase tracking-wider text-accent-emerald">picked</span>
                  )}
                </div>
                {Boolean(c.title) && <p className="text-xs font-semibold text-text-primary mb-1">{String(c.title)}</p>}
                <p className="text-sm text-text-primary">{String(c.body ?? c.line ?? '')}</p>
                {Boolean(c.rationale) && (
                  <p className="mt-2 text-xs text-muted italic">{String(c.rationale)}</p>
                )}
              </div>
            );
          })}
        </div>
      </div>
      {Boolean(o.chosenRationale) && (
        <Field label="Why this one won" value={String(o.chosenRationale)} />
      )}
    </div>
  );
}

function ReelTightenerOutput({ value }: { value: unknown }) {
  const o = asObj(value);
  if (!o) return <JsonFallback value={value} />;
  const script = String(o.script ?? '');
  const changes = asArr<string>(o.changes) ?? [];
  return (
    <div className="space-y-3 text-sm">
      <Field label="Tightened script" value={script} />
      {changes.length > 0 && (
        <>
          <SectionLabel>Changes made</SectionLabel>
          <ul className="list-disc list-inside text-xs text-text-primary space-y-0.5">
            {changes.map((c, i) => <li key={i}>{c}</li>)}
          </ul>
        </>
      )}
    </div>
  );
}

function FinalReaderOutput({ value }: { value: unknown }) {
  const o = asObj(value);
  if (!o) return <JsonFallback value={value} />;
  const verdict = String(o.verdict ?? '—');
  const tone = verdict === 'ship' ? 'bg-accent-emerald/20 text-accent-emerald' : 'bg-accent-amber/20 text-accent-amber';
  const strengths = asArr<string>(o.strengths) ?? [];
  const weaknesses = asArr<string>(o.weaknesses) ?? [];
  return (
    <div className="space-y-3 text-sm">
      <div className="flex items-center gap-3">
        <span className={`inline-block font-mono text-xs uppercase tracking-wider px-3 py-1 rounded ${tone}`}>{verdict}</span>
        {typeof o.scoreOutOf10 === 'number' && (
          <span className="font-mono text-xs text-muted">score {o.scoreOutOf10}/10</span>
        )}
      </div>
      {strengths.length > 0 && (
        <>
          <SectionLabel tone="emerald">Strengths</SectionLabel>
          <ul className="list-disc list-inside text-xs text-text-primary space-y-0.5">
            {strengths.map((s, i) => <li key={i}>{s}</li>)}
          </ul>
        </>
      )}
      {weaknesses.length > 0 && (
        <>
          <SectionLabel tone="red">Weaknesses</SectionLabel>
          <ul className="list-disc list-inside text-xs text-text-primary space-y-0.5">
            {weaknesses.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        </>
      )}
      {Boolean(o.reviseNotes) && (
        <Field label="Revision notes for the Editor" value={String(o.reviseNotes)} />
      )}
    </div>
  );
}

function StoryboardOutput({ value }: { value: unknown }) {
  const o = asObj(value);
  // v16: scenes + reelMood + moodReasoning. Fall back to v15 beats[]
  // shape for any historical runs in the database.
  const scenes = asArr<AnyObj>(o?.scenes);
  const beats = asArr<AnyObj>(o?.beats);
  const items = scenes ?? beats;
  if (!items) return <JsonFallback value={value} />;

  const reelMood = o?.reelMood ? String(o.reelMood) : null;
  const moodReasoning = o?.moodReasoning ? String(o.moodReasoning) : null;

  return (
    <div className="space-y-3">
      {reelMood && (
        <div className="flex items-center gap-3">
          <span className="font-mono text-[10px] uppercase tracking-wider text-muted">Reel mood</span>
          <span className="font-mono text-xs uppercase tracking-wider px-3 py-1 rounded bg-accent-cyan/15 text-accent-cyan">{reelMood}</span>
          {moodReasoning && <span className="text-xs text-muted italic">{moodReasoning}</span>}
        </div>
      )}
      <SectionLabel>{scenes ? 'Scenes' : 'Per-beat creative direction'}</SectionLabel>
      <ol className="space-y-3">
        {items.map((b, i) => {
          const dur = typeof b.durationFrames === 'number'
            ? `${(Number(b.durationFrames) / 24).toFixed(1)}s (${b.durationFrames} frames)`
            : typeof b.startMs === 'number' && typeof b.endMs === 'number'
              ? `${(Number(b.startMs) / 1000).toFixed(1)}s → ${(Number(b.endMs) / 1000).toFixed(1)}s`
              : '';
          return (
            <li key={i} className="rounded border border-border-color p-3 bg-surface-2/40">
              <div className="flex flex-wrap items-baseline gap-3 mb-2">
                <span className="font-mono text-[10px] uppercase tracking-wider text-accent-cyan">{i + 1}</span>
                <span className="font-mono text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-accent-cyan/15 text-accent-cyan">{String(b.key ?? '')}</span>
                {Boolean(b.motionArchetype) && (
                  <span className="font-mono text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-accent-amber/15 text-accent-amber">{String(b.motionArchetype)}</span>
                )}
                <span className="font-mono text-[9px] text-muted">{dur}</span>
              </div>
              {Boolean(b.voLine) && (
                <p className="text-xs text-muted mb-1">
                  <span className="font-mono uppercase tracking-wider text-[9px] mr-2">voLine</span>
                  "{String(b.voLine)}"
                </p>
              )}
              {Boolean(b.screenPhrase) && (
                <p className="text-xs text-accent-emerald mb-2">
                  <span className="font-mono uppercase tracking-wider text-[9px] mr-2 text-muted">on-screen</span>
                  {String(b.screenPhrase)}
                </p>
              )}
              {Boolean(b.spokenSnippet) && !b.voLine && (
                <p className="text-xs text-muted italic mb-2">audio: "{String(b.spokenSnippet)}"</p>
              )}
              <p className="text-sm text-text-primary leading-relaxed whitespace-pre-wrap">{String(b.direction ?? '')}</p>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function ReelMusicComposerOutput({ value }: { value: unknown }) {
  const o = asObj(value);
  if (!o) return <JsonFallback value={value} />;
  const track = asObj(o.track);
  return (
    <div className="space-y-3 text-sm">
      {track ? (
        <>
          <Field label="Track" value={String(track.label ?? track.id ?? '—')} />
          <Field label="File" value={`uploads/${String(track.fileName ?? '?')}`} />
          {asArr<string>(track.moods) && (
            <Field label="Moods" value={(track.moods as string[]).join(', ')} />
          )}
          {Boolean(track.attribution) && (
            <Field label="Attribution" value={String(track.attribution)} />
          )}
        </>
      ) : null}
      <Field label="Mount volume" value={String(o.volume ?? '0.10')} />
    </div>
  );
}

function ReelVoiceSynthesizerOutput({ value }: { value: unknown }) {
  const o = asObj(value);
  if (!o) return <JsonFallback value={value} />;
  const durations = asArr<number>(o.perSceneDurationsMs) ?? [];
  return (
    <div className="space-y-3 text-sm">
      <Field label="Total audio duration" value={`${((Number(o.totalDurationMs ?? 0)) / 1000).toFixed(2)}s`} />
      <Field label="Voice" value={String(o.voice ?? '—')} />
      <Field label="Model" value={String(o.model ?? '—')} />
      <Field label="TTS cost" value={`$${Number(o.usdCost ?? 0).toFixed(4)}`} />
      {durations.length > 0 && (
        <>
          <SectionLabel>Per-scene clip durations</SectionLabel>
          <ul className="text-xs text-muted font-mono space-y-0.5">
            {durations.map((d, i) => (
              <li key={i}>scene {i + 1}: {(Number(d) / 1000).toFixed(2)}s</li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

function ReelScriptOutput({ value }: { value: unknown }) {
  const o = asObj(value);
  if (!o) return <JsonFallback value={value} />;
  return (
    <div className="space-y-3 text-sm">
      <Field label="Topic" value={String(o.topicTitle ?? '—')} />
      <SectionLabel>VO script</SectionLabel>
      <p className="text-sm text-text-primary leading-relaxed whitespace-pre-wrap p-3 border border-border-color rounded bg-surface-2/40">
        {String(o.script ?? '')}
      </p>
    </div>
  );
}

function ReelArtDirectorOutput({ value }: { value: unknown }) {
  const o = asObj(value);
  if (!o) return <JsonFallback value={value} />;
  return (
    <div className="space-y-3 text-sm">
      <div className="flex flex-wrap items-baseline gap-3">
        <Field label="Validation" value={o.valid === true ? 'passed' : 'failed'} />
        {Boolean(o.model) && <Field label="Model" value={String(o.model)} />}
        {o.usdCost != null && <Field label="USD" value={`$${Number(o.usdCost).toFixed(4)}`} />}
      </div>
      {Boolean(o.validationError) && <Field label="Validation error" value={String(o.validationError)} />}
      <SectionLabel>Generated Remotion source</SectionLabel>
      <PromptBlock content={String(o.source ?? '(empty)')} />
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="font-mono text-[10px] uppercase tracking-wider text-muted mb-1">{label}</p>
      <p className="text-sm text-text-primary leading-relaxed">{value}</p>
    </div>
  );
}

// ───────────────────── Brief renderer (the assembled input) ─────────────────────
// Renders the "assembled brief" the agent received. Splits the object into
// named sections so it's readable. Long strings are kept verbatim; arrays
// of strings render as bullets; nested objects open inline.

function BriefRenderer({ value }: { value: unknown }) {
  const o = asObj(value);
  if (!o) {
    return <JsonFallback value={value} />;
  }
  return (
    <div className="space-y-3">
      {Object.entries(o).map(([key, val]) => (
        <BriefField key={key} keyName={key} value={val} />
      ))}
    </div>
  );
}

function BriefField({ keyName, value }: { keyName: string; value: unknown }) {
  if (value == null) return null;
  const label = humanise(keyName);
  if (typeof value === 'string') {
    if (value.length > 200) {
      return (
        <div>
          <p className="font-mono text-[10px] uppercase tracking-wider text-muted mb-1">{label}</p>
          <pre className="text-xs text-text-primary whitespace-pre-wrap leading-relaxed font-mono bg-surface-2/40 border border-border-color rounded p-3 max-h-[260px] overflow-auto scroll-thin">{value}</pre>
        </div>
      );
    }
    return <Field label={label} value={value} />;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return <Field label={label} value={String(value)} />;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return null;
    const allStrings = value.every((v) => typeof v === 'string');
    if (allStrings) {
      return (
        <div>
          <p className="font-mono text-[10px] uppercase tracking-wider text-muted mb-1">{label}</p>
          <ul className="list-disc list-inside text-xs text-text-primary space-y-0.5">
            {value.map((v, i) => <li key={i}>{String(v)}</li>)}
          </ul>
        </div>
      );
    }
    return (
      <details className="border border-border-color rounded p-2 bg-surface-2/40">
        <summary className="cursor-pointer font-mono text-[10px] uppercase tracking-wider text-muted">{label} ({value.length})</summary>
        <pre className="text-[11px] text-muted whitespace-pre-wrap leading-relaxed font-mono mt-2 max-h-[200px] overflow-auto scroll-thin">{JSON.stringify(value, null, 2)}</pre>
      </details>
    );
  }
  if (typeof value === 'object') {
    return (
      <details className="border border-border-color rounded p-2 bg-surface-2/40">
        <summary className="cursor-pointer font-mono text-[10px] uppercase tracking-wider text-muted">{label}</summary>
        <div className="mt-2 pl-2 border-l border-border-color">
          <BriefRenderer value={value} />
        </div>
      </details>
    );
  }
  return null;
}

function humanise(key: string): string {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (c) => c.toUpperCase())
    .trim();
}
